/**
 * Club benefits page - dynamic card states.
 *
 * Fetches /api/loyalty/summary + /cart.js and rewrites each benefit card
 * on /pages/club-benefits to match the customer's live loyalty state.
 * Cards are identified by data-card="points|gift|spa|birthday|voucher".
 *
 * State matrix per spec (Points benefit.docx):
 *   Points   : not-member | member >= minRedeem | member < minRedeem
 *   Gift     : not-member | eligible + cart>=min | eligible + cart<min | redeemed
 *   Spa      : not-member | member | member + birthday month (20%)
 *   Birthday : not-member/not-birth-month | birthday-eligible | birth-month redeemed
 *   Voucher  : not-member | welcome-eligible | member consumed | expired (renewal)
 */
(function () {
  var wrap = document.querySelector('.club-benefits-cards[data-club-benefits]');
  if (!wrap) return;

  var ctxEl = document.getElementById('club-benefits-context');
  var ctx = {};
  try { ctx = ctxEl ? JSON.parse(ctxEl.textContent || '{}') : {}; } catch (e) { ctx = {}; }

  var HEB_MONTHS = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];

  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  // Spec design uses DD.MM.YY (2-digit year) for redemption dates.
  function fmtDate(raw) {
    if (!raw) return '';
    var d = raw instanceof Date ? raw : new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + pad(d.getFullYear() % 100);
  }

  function findCard(type) {
    return wrap.querySelector('[data-card="' + type + '"]');
  }

  // Cache of monthly gift product images so we don't refetch on every
  // loyalty:updated event (fires on every cart change).
  var giftImagesCache = {};
  function fetchGiftImages(handle) {
    if (!handle) return Promise.resolve([]);
    if (giftImagesCache[handle]) return Promise.resolve(giftImagesCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var imgs = [];
        if (p && Array.isArray(p.images)) {
          // Product.js returns image src strings, media returns objects. Prefer
          // media (higher-res) when available, fall back to images.
          if (Array.isArray(p.media) && p.media.length) {
            imgs = p.media.filter(function (m) { return m && m.media_type === 'image' && m.src; }).map(function (m) { return m.src; });
          }
          if (!imgs.length) imgs = p.images.slice();
        }
        giftImagesCache[handle] = imgs;
        return imgs;
      })
      .catch(function () { return []; });
  }

  // Swap the benefit-illustration on the gift card to show the monthly gift
  // product's own images. Adds a lightweight swiper (dots + swipe) when the
  // product has 2+ images per the Figma spec. Idempotent — safe to call on
  // every render.
  function paintGiftImages(el, images) {
    if (!el) return;
    if (!images || !images.length) return;
    var illustration = el.querySelector('.benefit-illustration');
    if (!illustration) return;
    var signature = images.join('|');
    var existing = illustration.getAttribute('data-gift-signature') || '';
    if (existing === signature) return;
    // If the server-rendered fallback already shows this exact first image
    // (single-image case), don't remount — avoids a visible reflow flash
    // for members whose per-customer product matches the section-setting
    // fallback (the common case).
    if (images.length === 1 && existing.split('|').indexOf(images[0]) !== -1) {
      illustration.setAttribute('data-gift-signature', signature);
      return;
    }
    illustration.setAttribute('data-gift-signature', signature);
    illustration.classList.add('benefit-illustration--gift');
    if (images.length === 1) {
      illustration.innerHTML = '<img src="' + images[0] + '" alt="" loading="lazy">';
      return;
    }
    var slidesHtml = images.map(function (src, i) {
      return '<div class="gift-slide' + (i === 0 ? ' is-active' : '') + '"><img src="' + src + '" alt="" loading="lazy"></div>';
    }).join('');
    var dotsHtml = images.map(function (_, i) {
      return '<button type="button" class="gift-dot' + (i === 0 ? ' is-active' : '') + '" data-idx="' + i + '" aria-label="Image ' + (i + 1) + '"></button>';
    }).join('');
    illustration.innerHTML =
      '<div class="gift-swiper" data-active="0">' +
        '<div class="gift-slides">' + slidesHtml + '</div>' +
        '<div class="gift-dots">' + dotsHtml + '</div>' +
      '</div>';
    wireGiftSwiper(illustration.querySelector('.gift-swiper'), images.length);
  }

  function wireGiftSwiper(root, count) {
    if (!root) return;
    var slides = root.querySelectorAll('.gift-slide');
    var dots = root.querySelectorAll('.gift-dot');
    function go(idx) {
      if (idx < 0) idx = count - 1;
      if (idx >= count) idx = 0;
      root.setAttribute('data-active', String(idx));
      slides.forEach(function (s, i) { s.classList.toggle('is-active', i === idx); });
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
    }
    dots.forEach(function (d) {
      d.addEventListener('click', function () { go(parseInt(d.getAttribute('data-idx'), 10) || 0); });
    });
    var startX = 0, tracking = false;
    root.addEventListener('touchstart', function (e) {
      if (!e.touches || !e.touches.length) return;
      startX = e.touches[0].clientX; tracking = true;
    }, { passive: true });
    root.addEventListener('touchend', function (e) {
      if (!tracking) return;
      tracking = false;
      var endX = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0].clientX : startX;
      var dx = endX - startX;
      if (Math.abs(dx) < 30) return;
      var current = parseInt(root.getAttribute('data-active'), 10) || 0;
      // RTL: swipe left → next; swipe right → prev.
      go(current + (dx < 0 ? 1 : -1));
    }, { passive: true });
  }

  function ensureFooter(el) {
    var footer = el.querySelector('.benefit-footer');
    if (!footer) {
      footer = document.createElement('div');
      footer.className = 'benefit-footer';
      var main = el.querySelector('.benefit-main');
      if (main) main.appendChild(footer);
    }
    return footer;
  }

  function setCard(el, s) {
    if (!el) return;
    var labelEl = el.querySelector('.benefit-label');
    var titleEl = el.querySelector('.benefit-title');
    var descEl  = el.querySelector('.benefit-desc');

    if (s.label != null && labelEl) labelEl.textContent = s.label;
    if (s.title != null && titleEl) titleEl.textContent = s.title;
    if (s.desc  != null && descEl)  descEl.textContent  = s.desc;
    if (s.highlight != null) el.classList.toggle('benefit-card--highlight', !!s.highlight);
    el.classList.toggle('benefit-card--redeemed', s.statusVariant === 'redeemed');

    var existingFooter = el.querySelector('.benefit-footer');
    if (s.statusText) {
      var footer = ensureFooter(el);
      footer.innerHTML = '';
      var span = document.createElement('span');
      span.className = 'benefit-status' + (s.statusVariant ? ' benefit-status--' + s.statusVariant : '');
      span.textContent = s.statusText;
      footer.appendChild(span);
    } else if (s.linkText) {
      var footer2 = ensureFooter(el);
      footer2.innerHTML = '';
      var a = document.createElement('a');
      a.className = 'benefit-link';
      a.href = s.linkUrl || 'javascript:;';
      a.textContent = s.linkText;
      footer2.appendChild(a);
    } else if (existingFooter) {
      existingFooter.remove();
    }
  }

  // Reuse the summary + cart the loyalty strip already fetches — the strip
  // dispatches `loyalty:updated` on every refresh and mirrors the latest
  // values on window.__loyaltySummary / window.__loyaltyCart. Avoids a
  // duplicate POST /api/loyalty/summary on this page.
  //
  // The strip's cart shape is normalized ({ total, pts, items }) but the
  // gift card only needs total_price in cents, which is what /cart.js
  // returns. We call render with whichever cart shape we get.
  function render(summary, cart) {
    // Normalise the two cart shapes we can receive:
    //   - Strip normalized form: { total, originalTotal, ... } (₪, floats)
    //   - Raw /cart.js: { total_price, original_total_price, items_subtotal_price } (cents)
    // Downstream reads cart.total_price (final, in cents) and cart.original_total_price
    // (pre-discount, in cents). Build both regardless of which shape came in.
    if (cart && cart.total != null && cart.total_price == null) {
      cart = {
        total_price: Math.round(cart.total * 100),
        original_total_price: Math.round((cart.originalTotal != null ? cart.originalTotal : cart.total) * 100),
        // Preserve items so the effective-member check below can spot the
        // club variant in the cart. The strip's normalised shape uses the
        // same `items` field, so this passes through both shapes.
        items: Array.isArray(cart.items) ? cart.items : [],
        // Preserve hasClubProduct — the strip already computed this while
        // fetching /cart.js. Dropping it during normalisation was the
        // reason state.js kept rendering the guest state for customers
        // who had the membership sitting in their cart.
        hasClubProduct: cart.hasClubProduct === true
      };
    } else if (cart && cart.total_price != null && cart.original_total_price == null) {
      cart.original_total_price = cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price;
    }
    _render(summary || {}, cart || { total_price: 0, original_total_price: 0 });
    // Wait one frame so browser paints the updated DOM before we hide
    // the page loader — prevents the flash of pre-render content.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        try {
          document.dispatchEvent(new CustomEvent('club-benefits:ready'));
          if (typeof window.hideLottieLoader === 'function') window.hideLottieLoader();
        } catch (e) {}
      });
    });
  }

  var rendered = false;
  document.addEventListener('loyalty:updated', function (evt) {
    rendered = true;
    var d = (evt && evt.detail) || {};
    render(d.summary, d.cart);
  });

  // If the strip has already fired before this file loaded, use the cached
  // values immediately. Otherwise wait for the event.
  if (window.__loyaltySummary || window.__loyaltyCart) {
    rendered = true;
    render(window.__loyaltySummary, window.__loyaltyCart);
  }

  // A logged-out visitor has no loyalty state to wait for, so paint the
  // not-member cards straight away instead of holding the static Liquid copy
  // for the 3s fallback below.
  if (ctx.isGuest && !rendered) {
    rendered = true;
    render(null, null);
  }

  // Fallback: if 3s pass without the strip broadcasting (e.g. strip disabled
  // for this customer), render the guest state so cards don't stay static.
  setTimeout(function () {
    if (!rendered) render(null, null);
  }, 3000);

  function _render(summary, cart) {
    var isMember   = !!summary.isClubMember;
    var isExpired  = !!summary.isExpired;
    // "Effective active": server-side active member OR the club-membership
    // variant is currently in the cart (customer is joining / renewing). The
    // backend `isClubMember`/`isExpired` flags don't flip until Comax sync,
    // so without this the benefits page keeps rendering the pre-join state
    // long after the customer added the membership to their cart.
    var cartHasClub = false;
    try {
      // 1. Trust the strip's precomputed flag when the summary broadcast
      //    included it — it already resolved by product id (drawer path).
      if (cart && cart.hasClubProduct === true) {
        cartHasClub = true;
      } else if (cart && Array.isArray(cart.items)) {
        // 2. Fall back to walking cart.items. Product id catches every
        //    variant of the membership (1-year, 2-year …); variant id is
        //    only a secondary check for the first-available variant.
        var body = document.body;
        var clubPid = String((body && body.getAttribute('data-club-product-id')) || '').trim();
        var clubVid = String((body && body.getAttribute('data-club-variant-id')) || '').trim();
        cartHasClub = cart.items.some(function (i) {
          if (clubPid && String(i.product_id) === clubPid) return true;
          if (clubVid && String(i.variant_id) === clubVid) return true;
          return false;
        });
      }
      // 3. Last-resort: the body class that club-effective-state.js sets
      //    from its own /cart.js poll. Guards against a stale broadcast.
      if (!cartHasClub && document.body && document.body.classList.contains('is-club-member--effective') && !(isMember && !isExpired)) {
        cartHasClub = true;
      }
    } catch (e) {}
    var active     = (isMember && !isExpired) || cartHasClub;
    var cartTotal  = (cart && cart.total_price ? cart.total_price : 0) / 100;
    // Pre-discount cart total for eligibility gates (gift threshold) — a
    // birthday / redemption coupon shouldn't push the customer below the
    // ₪250 gift threshold after already qualifying.
    var cartEligibleTotal = (cart && cart.original_total_price != null ? cart.original_total_price : (cart && cart.total_price) || 0) / 100;
    var bp         = summary.balancePoints || 0;
    var minRedeem  = summary.minRedeem || 50;
    var giftMin    = summary.monthlyGiftMinAmount || 200;
    // Product name + handle: prefer the live summary (per-customer), fall
    // back to the section-setting product picker so guests and any
    // API-unavailable case still see the correct current gift.
    var giftName   = summary.monthlyGiftDefaultProductTitle || ctx.giftTitle || ctx.giftName || '';
    var giftHandle = summary.monthlyGiftDefaultProductHandle || ctx.giftHandle || '';
    var birthMonth = ctx.birthMonth ? parseInt(ctx.birthMonth, 10) : 0;
    var currentMonth = new Date().getMonth() + 1;
    var isBirthMonth = birthMonth === currentMonth;
    var birthMonthName = birthMonth ? HEB_MONTHS[birthMonth - 1] : '';
    var pct = summary.birthdayPercentage || 20;
    var amount = summary.welcomeVoucherAmount || 40;
    // Benefit blackouts (admin-set dates when the birthday coupon and/or the
    // welcome voucher can't be used — set independently). While one is
    // running the API sends birthdayBlackoutUntil / welcomeBlackoutUntil (ISO)
    // and birthdayEligible / welcomeVoucherEligible are already false, but the
    // birthday + spa cards also fall back to the client-side DOB month, so they
    // must check this explicitly. benefitBlackoutUntil = older API (birthday).
    function parseDate(raw) {
      var d = raw ? new Date(raw) : null;
      return d && !isNaN(d.getTime()) ? d : null;
    }
    // "DD.MM.YY HH:mm" always in Israel time (blackout dates are set in Israel
    // time), whatever timezone the visitor's browser is in.
    function fmtIsraelDateTime(d) {
      if (!d) return '';
      try {
        var bp = {};
        new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: '2-digit',
          hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
        }).formatToParts(d).forEach(function (x) { bp[x.type] = x.value; });
        return bp.day + '.' + bp.month + '.' + bp.year + ' ' + bp.hour + ':' + bp.minute;
      } catch (e) {
        return fmtDate(d) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
      }
    }
    var blackoutUntil = parseDate(summary.birthdayBlackoutUntil || summary.benefitBlackoutUntil);
    var blackoutUntilText = fmtIsraelDateTime(blackoutUntil);
    var welcomeBlackoutUntil = parseDate(summary.welcomeBlackoutUntil);
    var welcomeBlackoutUntilText = fmtIsraelDateTime(welcomeBlackoutUntil);
    var spaUrl = ctx.spaUrl || '/collections/spa-loccitane';

    // === Points ===
    var points = findCard('points');
    if (points) {
      if (!active) {
        setCard(points, {
          label: 'נקודות',
          title: '1 נקודה = ₪1',
          desc: 'ניתן לצבור נקודות ברכישה זו לאחר ההצטרפות למועדון.',
          statusText: '', highlight: false
        });
      } else if (bp >= minRedeem) {
        setCard(points, {
          label: 'נקודות',
          title: bp + ' נקודות = ₪' + bp,
          desc: 'ניתן לממש נקודות ברכישה זו. המימוש יתבצע בשלב התשלום.',
          statusText: 'זמין למימוש', statusVariant: 'active', highlight: true
        });
      } else {
        var need = minRedeem - bp;
        setCard(points, {
          label: 'נקודות',
          title: bp + ' נקודות',
          desc: 'כבר צברת ' + bp + ' נקודות! עוד ' + need + ' נקודות לצבירה כדי לממש אותן.',
          statusText: 'זמין למימוש מעל ' + minRedeem + ' נקודות',
          statusVariant: 'pending', highlight: true
        });
      }
    }

    // === Gift ===
    // Four states per Figma:
    //   1. Effective member + cart ≥ giftMin, not yet redeemed → "in your cart" active pill
    //   2. Effective member + cart < giftMin, not yet redeemed → "need X more" pending pill
    //      (also covers the joining case — cartHasClub flips `active`)
    //   3. Effective member + already redeemed this month → "מומש" redeemed pill
    //   4. Non-member (no membership in cart either) → generic pitch, no pill
    // Redeemed check comes first so a joining customer never shows "מומש".
    var gift = findCard('gift');
    if (gift) {
      // Replace the static illustration with the actual monthly gift product's
      // image(s) — fires once per handle, cached thereafter.
      if (giftHandle) {
        fetchGiftImages(giftHandle).then(function (imgs) { paintGiftImages(gift, imgs); });
      }
      var giftTitle = giftName || 'מתנת המועדון החודשית';
      if (!active) {
        setCard(gift, {
          label: 'מתנת מועדון',
          title: giftTitle,
          desc: 'מתנה מפרובאנס בכל חודש: מוצר אהוב במתנה בקנייה הראשונה, והזדמנות לגלות ולהתאהב במוצרים חדשים.',
          statusText: '', highlight: false
        });
      } else if (summary.monthlyGiftRedeemedAt) {
        var giftRedeemedDate = fmtDate(summary.monthlyGiftRedeemedAt);
        setCard(gift, {
          label: 'מתנת מועדון',
          title: giftTitle,
          desc: 'ההטבה מומשה ב-' + giftRedeemedDate + '. חכו למתנה חדשה בחודש הבא!',
          statusText: 'מומש', statusVariant: 'redeemed', highlight: false
        });
      } else if (cartEligibleTotal >= giftMin) {
        setCard(gift, {
          label: 'מתנת מועדון',
          title: giftTitle,
          desc: 'איזה כיף, מתנת המועדון החודשית אצלך בסל!',
          statusText: 'זמין למימוש', statusVariant: 'active', highlight: true
        });
      } else {
        var diff = Math.max(0, Math.ceil(giftMin - cartEligibleTotal));
        setCard(gift, {
          label: 'מתנת מועדון',
          title: giftTitle,
          desc: 'רק עוד ' + diff + '₪ והמתנה החודשית של המועדון אצלך בסל!',
          statusText: 'זמין למימוש ברכישה מעל ' + giftMin + '₪',
          statusVariant: 'pending', highlight: true
        });
      }
    }

    // === Spa ===
    // Three states per Figma:
    //   1. Effective member in birth month AND birthday coupon not yet
    //      redeemed → 20% face+body, birthday copy
    //   2. Effective member otherwise → 10% face+body, permanent copy
    //   3. Not (yet) a member → 10% face+body, "available to club members" copy
    // Redemption consumes the birthday-tier spa discount too — once the
    // customer redeems the 20% birthday coupon, spa reverts to the 10%
    // year-round club rate for the rest of the month.
    var spaRedeemed = !!summary.birthdayCouponRedeemedAt;
    var spaInBirthMonth = (summary.birthdayEligible === true || isBirthMonth) && !spaRedeemed && !blackoutUntil;
    var spa = findCard('spa');
    if (spa) {
      if (active && spaInBirthMonth) {
        setCard(spa, {
          label: 'הטבת ספא ל\'אוקסיטן',
          title: 'טיפולי פנים וגוף ב-20% הנחה',
          desc: 'חוויית ספא מרגיעה עם המוצרים האייקוניים. בתוקף בחודש יום ההולדת.',
          linkText: 'לכל הטיפולים', linkUrl: spaUrl, highlight: true
        });
      } else if (active) {
        setCard(spa, {
          label: 'הטבת ספא ל\'אוקסיטן',
          title: 'טיפולי פנים וגוף ב-10% הנחה',
          desc: 'חוויית ספא מרגיעה עם המוצרים האייקוניים. הנחה קבועה במועדון.',
          linkText: 'לכל הטיפולים', linkUrl: spaUrl, highlight: true
        });
      } else {
        setCard(spa, {
          label: 'הטבת ספא ל\'אוקסיטן',
          title: 'טיפולי פנים וגוף ב-10% הנחה',
          desc: 'חוויית ספא מרגיעה עם המוצרים האייקוניים. זמין לחברי מועדון.',
          linkText: 'לכל הטיפולים', linkUrl: spaUrl, highlight: false
        });
      }
    }

    // === Birthday ===
    // Three states, driven by "in birth month" (any of: API birthdayEligible,
    // API birthdayCouponRedeemedAt truthy, or client DOB month === current):
    //   1. Effective member in birthday month + not redeemed → 'פעיל' active pill
    //   2. Effective member in birthday month + has redemption date → 'מומש'
    //   3. Not birthday month OR not an effective member → general screen
    //
    // We prefer the API signals because the server can flag birth-month
    // eligibility even when the customer's DOB metafield isn't populated
    // client-side (e.g. staging accounts). Falling back to isBirthMonth keeps
    // the page useful if the API returns partial data.
    var birthdayRedeemed = !!summary.birthdayCouponRedeemedAt;
    var inBirthMonth = summary.birthdayEligible === true || birthdayRedeemed || isBirthMonth;
    // Prefer the API/client-known month name; if only the API flag is set and
    // client DOB isn't wired, fall back to the current month name so the
    // "valid for X only" line still reads correctly.
    var birthMonthLabel = birthMonthName || HEB_MONTHS[currentMonth - 1] || '';
    var bday = findCard('birthday');
    if (bday) {
      if (active && inBirthMonth && birthdayRedeemed) {
        setCard(bday, {
          label: 'הטבת יום הולדת 🎂',
          title: pct + '% הנחה על כל הקנייה',
          desc: 'תקפה לחודש ' + birthMonthLabel + ' בלבד. מומשה ב-' + fmtDate(summary.birthdayCouponRedeemedAt) + '.',
          statusText: 'מומש', statusVariant: 'redeemed', highlight: false
        });
      } else if (active && inBirthMonth && blackoutUntil) {
        setCard(bday, {
          label: 'הטבת יום הולדת 🎂',
          title: pct + '% הנחה על כל הקנייה',
          desc: 'הטבת יום ההולדת אינה זמינה בתאריכים אלו. ניתן יהיה לממש אותה החל מ-' + blackoutUntilText + '.',
          statusText: 'לא זמין כעת', statusVariant: 'redeemed', highlight: false
        });
      } else if (active && inBirthMonth) {
        setCard(bday, {
          label: 'הטבת יום הולדת 🎂',
          title: pct + '% הנחה על כל הקנייה',
          desc: 'תקפה לחודש ' + birthMonthLabel + ' בלבד. המימוש מתבצע אוטומטית בשלב התשלום.',
          statusText: 'פעיל', statusVariant: 'active', highlight: true
        });
      } else {
        setCard(bday, {
          label: 'הטבת יום הולדת 🎂',
          title: pct + '% הנחה על כל הקנייה',
          desc: 'למימוש במהלך חודש יום ההולדת לאחר ההצטרפות למועדון.',
          statusText: '', highlight: false
        });
      }
    }

    // === Welcome / renewal voucher ===
    var voucher = findCard('voucher');
    if (voucher) {
      if (isMember && isExpired) {
        setCard(voucher, {
          label: 'הטבת חידוש',
          title: 'שובר חידוש ₪' + amount,
          desc: 'למימוש במהלך החודש העוקב לאחר חידוש המועדון.',
          statusText: '', highlight: false
        });
      } else if (!isMember) {
        setCard(voucher, {
          label: 'הטבת הצטרפות',
          title: 'שובר הצטרפות ₪' + amount,
          desc: 'למימוש במהלך החודש העוקב לאחר ההצטרפות למועדון.',
          statusText: '', highlight: false
        });
      } else if (welcomeBlackoutUntil && summary.welcomeBlockedByBlackout) {
        // Member who has the voucher, but a welcome-voucher blackout is running.
        setCard(voucher, {
          label: 'הטבת הצטרפות',
          title: 'שובר הצטרפות ₪' + amount,
          desc: 'שובר ההצטרפות אינו זמין בתאריכים אלו. ניתן יהיה לממש אותו החל מ-' + welcomeBlackoutUntilText + '.',
          statusText: 'לא זמין כעת', statusVariant: 'redeemed', highlight: false
        });
      } else if (summary.welcomeVoucherEligible) {
        var expiry = '';
        if (summary.welcomeVoucherExpiresAt) {
          expiry = fmtDate(summary.welcomeVoucherExpiresAt);
        } else if (summary.welcomeVoucherDaysLeft != null) {
          var d = new Date();
          d.setDate(d.getDate() + Number(summary.welcomeVoucherDaysLeft));
          expiry = fmtDate(d);
        }
        setCard(voucher, {
          label: 'הטבת הצטרפות',
          title: 'שובר הצטרפות ₪' + amount,
          desc: 'מחכה לך ₪' + amount + ' הנחה לקנייה הקרובה' + (expiry ? '. בתוקף עד ' + expiry : '') + '.',
          statusText: 'זמין למימוש', statusVariant: 'active', highlight: true
        });
      } else if (summary.welcomeVoucherRedeemedAt) {
        setCard(voucher, {
          label: 'הטבת הצטרפות',
          title: 'שובר הצטרפות ₪' + amount,
          desc: 'הטבת ההצטרפות מומשה בהצלחה ב-' + fmtDate(summary.welcomeVoucherRedeemedAt) + '.',
          statusText: 'מומש', statusVariant: 'redeemed', highlight: false
        });
      } else {
        // Active member without a voucher and no redemption date — either
        // the voucher window has expired unused or this member joined
        // before the voucher program existed. Fall back to the neutral
        // 'general' copy (same as the not-joined description) so we don't
        // falsely claim it was redeemed.
        setCard(voucher, {
          label: 'הטבת הצטרפות',
          title: 'שובר הצטרפות ₪' + amount,
          desc: 'למימוש במהלך החודש העוקב לאחר ההצטרפות למועדון.',
          statusText: '', highlight: false
        });
      }
    }
  }
})();
